import { useState } from "react";
import { BadgeCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { formatCnpj, formatDateHoraBr } from "@/lib/format";
import { useFarmaciasPendentes, useValidarFarmacia, type FarmaciaPendente } from "./api";
import { formatNumero } from "./cidades";
import { ConsultaEstabelecimento } from "./ConsultaAnvisa";
import { rotuloEstabelecimento } from "./schema";

/**
 * Substitui a rotina manual do ddl/004 do dispensacao: conferir o CNPJ na Anvisa e
 * marcar o cadastro como VALIDADO. PENDENTE não bloqueia nada na farmácia (ela já
 * dispensa normalmente); validar é só o registro de que a equipe conferiu.
 */
export function FarmaciasPendentes() {
  const pendentes = useFarmaciasPendentes();
  const validar = useValidarFarmacia();
  const [confirmando, setConfirmando] = useState<FarmaciaPendente | null>(null);

  function confirmar() {
    if (!confirmando) return;
    const farmacia = confirmando;
    validar.mutate(farmacia.id, {
      onSuccess: () => toast.success(`${farmacia.nomeFantasia ?? farmacia.razaoSocial} validada.`),
    });
  }

  return (
    <section className="rounded-xl border border-border bg-card p-5 shadow-card">
      <div className="mb-4 flex items-center gap-2">
        <BadgeCheck className="h-5 w-5 text-primary" aria-hidden />
        <h2 className="text-base font-semibold text-foreground">
          Farmácias pendentes de validação ({pendentes.data?.length ?? "…"})
        </h2>
      </div>

      {pendentes.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Farmácia</TableHead>
              <TableHead>CNPJ</TableHead>
              <TableHead>Cidade</TableHead>
              <TableHead>Responsável técnico</TableHead>
              <TableHead>Cadastro</TableHead>
              <TableHead className="text-right">Dispensações</TableHead>
              <TableHead className="w-48" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {(pendentes.data ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground">
                  Nenhuma farmácia aguardando validação.
                </TableCell>
              </TableRow>
            ) : (
              pendentes.data!.map((f) => (
                <TableRow key={f.id}>
                  <TableCell>
                    <span className="font-medium">{f.nomeFantasia ?? f.razaoSocial}</span>
                    {f.nomeFantasia && <span className="block text-xs text-muted-foreground">{f.razaoSocial}</span>}
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-mono text-xs">
                    {f.cnpj ? formatCnpj(f.cnpj) : "—"}
                    {f.publica && (
                      <span className="block text-muted-foreground">
                        CNES {f.cnes}
                        <Badge variant="outline" className="ml-2 font-sans">
                          pública
                        </Badge>
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{f.cidade && `${f.cidade}/${f.estado}`}</TableCell>
                  <TableCell>
                    {f.nomeResponsavelTecnico ?? "—"}
                    {f.crfResponsavelTecnico && (
                      <span className="block text-xs text-muted-foreground">
                        CRF {f.crfResponsavelTecnico}/{f.ufCrfResponsavelTecnico}
                      </span>
                    )}
                    {f.emailResponsavelTecnico && (
                      <span className="block text-xs text-muted-foreground">
                        {f.emailResponsavelTecnico}
                        {f.telefoneResponsavelTecnico && ` · ${f.telefoneResponsavelTecnico}`}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{formatDateHoraBr(f.dataCadastro)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatNumero(f.dispensacoes)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <ConsultaEstabelecimento cnpj={f.cnpj} cnes={f.cnes} />
                      <Button size="sm" onClick={() => setConfirmando(f)} disabled={validar.isPending}>
                        Validar
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <ConfirmDialog
        open={confirmando !== null}
        onOpenChange={(open) => !open && setConfirmando(null)}
        title="Validar cadastro da farmácia?"
        description={
          confirmando && (
            <>
              {confirmando.nomeFantasia ?? confirmando.razaoSocial} — {rotuloEstabelecimento(confirmando)}.{" "}
              {confirmando.cnpj
                ? "Confirme que o CNPJ foi conferido na Anvisa."
                : "Confirme que o CNES foi conferido na consulta do DATASUS."}{" "}
              O cadastro passa de pendente para validado.
            </>
          )
        }
        confirmLabel="Validar"
        onConfirm={confirmar}
      />
    </section>
  );
}
